import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import {
  Users, Mic, Settings, LogOut, Download, Search, ArrowUpDown,
  ChevronRight, X, FileText, Clock, MailPlus, Handshake, Link as LinkIcon,
  ShieldCheck,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  US_STATES, YEARS_IN_BUSINESS, TSHIRT_SIZES, HOW_HEARD, TALK_CATEGORIES, TALK_LENGTHS,
} from "@/lib/formOptions";
import CampaignsTab from "@/pages/admin/CampaignsTab";
import LinksTab from "@/pages/admin/LinksTab";
import WaiversTab from "@/pages/admin/WaiversTab";

const INT_STATUSES = ["Interested", "Invited", "Confirmed", "Declined"];
const SPK_STATUSES = ["Under Review", "Invited to Interview", "Accepted", "Declined"];
const SPONSOR_STATUSES = ["New", "Contacted", "Confirmed", "Declined"];
const SPONSOR_TIERS_ADMIN = [
  { slug: "title-partner", label: "Title Partner" },
  { slug: "supporting-partner", label: "Supporting Partner" },
  { slug: "community-sponsor", label: "Community Sponsor" },
];

const STATUS_COLORS = {
  "Interested": "bg-gray-100 text-gray-700 border-gray-200",
  "Invited": "bg-amber-50 text-amber-700 border-amber-200",
  "Confirmed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Accepted": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Declined": "bg-rose-50 text-rose-700 border-rose-200",
  "Under Review": "bg-gray-100 text-gray-700 border-gray-200",
  "Invited to Interview": "bg-amber-50 text-amber-700 border-amber-200",
  "New": "bg-gray-100 text-gray-700 border-gray-200",
  "Contacted": "bg-amber-50 text-amber-700 border-amber-200",
};

function StatusTag({ status }) {
  return (
    <span data-testid={`status-${status}`} className={`inline-block px-2 py-0.5 text-xs font-semibold rounded border ${STATUS_COLORS[status] || "bg-gray-100 text-gray-700 border-gray-200"}`}>
      {status}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function DetailDrawer({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex">
      <div onClick={onClose} className="absolute inset-0 bg-black/30" data-testid="drawer-backdrop" />
      <div className="ml-auto w-full max-w-xl bg-white h-full overflow-y-auto shadow-xl relative">
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between">
          <h3 className="font-display-tight text-lg text-[#0A1128]">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900" data-testid="drawer-close">
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-6">{children}</div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="grid grid-cols-3 gap-4 py-2.5 border-b border-[#F3F4F6] text-sm">
      <div className="text-[#6B7280]">{label}</div>
      <div className="col-span-2 text-[#0A1128] font-medium break-words">{value}</div>
    </div>
  );
}

function downloadCsv(path) {
  fetch(`${API}${path}`, { credentials: "include" })
    .then(async (r) => {
      if (!r.ok) throw new Error("Export failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = path.includes("speakers")
        ? "speakers.csv"
        : path.includes("sponsors")
        ? "sponsors.csv"
        : path.includes("waivers")
        ? "waivers.csv"
        : "registrations.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    })
    .catch(() => toast.error("Export failed"));
}

/* -------------------- REGISTRATIONS TAB -------------------- */
function RegistrationsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ state: "", years_in_business: "", tshirt_size: "", food_allergies: "", how_heard: "", status: "", panel_apply: "", stay_on_property: "" });
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search: search || undefined, ...filters, sort_by: sortBy, sort_dir: sortDir };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const { data } = await api.get("/admin/interest", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  }, [search, filters, sortBy, sortDir]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/interest/${id}`, { status });
      toast.success("Status updated");
      setSelected((s) => s && s.id === id ? { ...s, status } : s);
      fetchData();
    } catch (e) { toast.error("Update failed"); }
  };

  const sortHeader = (label, key) => (
    <th
      className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280] cursor-pointer hover:text-[#0066FF]"
      onClick={() => { if (sortBy === key) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(key); setSortDir("asc"); } }}
      data-testid={`sort-${key}`}
    >
      <span className="inline-flex items-center gap-1">{label} <ArrowUpDown size={12} /></span>
    </th>
  );

  return (
    <div data-testid="registrations-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display-tight text-2xl text-[#0A1128]">Interest Registrations</h2>
          <p className="text-sm text-[#6B7280] mt-1" data-testid="registrations-total">{total} total registrations</p>
        </div>
        <button onClick={() => downloadCsv("/admin/interest/export")} data-testid="export-registrations"
          className="inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors">
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 mb-4">
        {/* Quick filter chips */}
        <div className="flex flex-wrap items-center gap-2 mb-4 pb-3 border-b border-[#F3F4F6]">
          <span className="text-xs font-bold uppercase tracking-widest text-[#6B7280] mr-1">Quick filters:</span>
          <button
            type="button"
            data-testid="chip-all"
            onClick={() => setFilters({ state: "", years_in_business: "", tshirt_size: "", food_allergies: "", how_heard: "", status: "", panel_apply: "", stay_on_property: "" })}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              !Object.values(filters).some(Boolean)
                ? "bg-[#0A1128] text-white"
                : "bg-[#F3F4F6] text-[#0A1128] hover:bg-[#E5E7EB]"
            }`}
          >
            All registrations
          </button>
          <button
            type="button"
            data-testid="chip-panel-applicants"
            onClick={() => setFilters({ ...filters, panel_apply: filters.panel_apply === "Yes" ? "" : "Yes" })}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              filters.panel_apply === "Yes"
                ? "bg-[#FACC15] text-[#0A1128]"
                : "bg-[#FACC15]/15 text-[#A16207] hover:bg-[#FACC15]/30"
            }`}
          >
            🎤 Panel applicants
          </button>
          <button
            type="button"
            data-testid="chip-stay-property"
            onClick={() => setFilters({ ...filters, stay_on_property: filters.stay_on_property === "Yes" ? "" : "Yes" })}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              filters.stay_on_property === "Yes"
                ? "bg-[#0066FF] text-white"
                : "bg-[#0066FF]/10 text-[#0066FF] hover:bg-[#0066FF]/20"
            }`}
          >
            🛏️ Wants on-property lodging
          </button>
          <button
            type="button"
            data-testid="chip-allergies"
            onClick={() => setFilters({ ...filters, food_allergies: filters.food_allergies === "Yes" ? "" : "Yes" })}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              filters.food_allergies === "Yes"
                ? "bg-amber-500 text-white"
                : "bg-amber-100 text-amber-700 hover:bg-amber-200"
            }`}
          >
            ⚠️ Has dietary needs
          </button>
        </div>

        <div className="grid md:grid-cols-6 gap-3">
          <div className="md:col-span-2 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input className="pl-9 h-10" placeholder="Search name, email, school…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="reg-search" />
          </div>
          <Select value={filters.state || "all"} onValueChange={(v) => setFilters({ ...filters, state: v === "all" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="filter-state"><SelectValue placeholder="State" /></SelectTrigger>
            <SelectContent className="max-h-72"><SelectItem value="all">All states</SelectItem>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.years_in_business || "all"} onValueChange={(v) => setFilters({ ...filters, years_in_business: v === "all" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="filter-years"><SelectValue placeholder="Years in business" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All years</SelectItem>{YEARS_IN_BUSINESS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.tshirt_size || "all"} onValueChange={(v) => setFilters({ ...filters, tshirt_size: v === "all" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="filter-shirt"><SelectValue placeholder="T-Shirt" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All sizes</SelectItem>{TSHIRT_SIZES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.food_allergies || "all"} onValueChange={(v) => setFilters({ ...filters, food_allergies: v === "all" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="filter-allergies"><SelectValue placeholder="Allergies" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
          </Select>
          <Select value={filters.how_heard || "all"} onValueChange={(v) => setFilters({ ...filters, how_heard: v === "all" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="filter-how-heard"><SelectValue placeholder="How heard" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All sources</SelectItem>{HOW_HEARD.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.panel_apply || "all"} onValueChange={(v) => setFilters({ ...filters, panel_apply: v === "all" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="filter-panel-apply"><SelectValue placeholder="Panel" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="Yes">Panel applicants only</SelectItem><SelectItem value="No">Non-applicants only</SelectItem></SelectContent>
          </Select>
          <Select value={filters.stay_on_property || "all"} onValueChange={(v) => setFilters({ ...filters, stay_on_property: v === "all" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="filter-stay-property"><SelectValue placeholder="Lodging" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="Yes">Wants on-property</SelectItem><SelectItem value="No">Booking own</SelectItem><SelectItem value="Not sure">Not sure</SelectItem></SelectContent>
          </Select>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{INT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E5E7EB]">
            <thead className="bg-[#F9FAFB]">
              <tr>
                {sortHeader("Submitted", "created_at")}
                {sortHeader("Name", "first_name")}
                {sortHeader("Email", "email")}
                {sortHeader("State", "state")}
                {sortHeader("School", "business_name")}
                {sortHeader("Yrs Biz", "years_in_business")}
                {sortHeader("Students/Wk", "students_per_week")}
                {sortHeader("Shirt", "tshirt_size")}
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Panel</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Stay</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Allergies</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Heard</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Status</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {loading ? (
                <tr><td colSpan={14} className="px-3 py-8 text-center text-sm text-[#6B7280]">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={14} className="px-3 py-8 text-center text-sm text-[#6B7280]">No registrations yet.</td></tr>
              ) : items.map((r) => (
                <tr key={r.id} className="hover:bg-[#F9FAFB] cursor-pointer" onClick={() => setSelected(r)} data-testid={`reg-row-${r.id}`}>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{formatDate(r.created_at)}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] font-medium whitespace-nowrap">{r.first_name} {r.last_name}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{r.email}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{r.state}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128]">{r.business_name}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{r.years_in_business}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{r.students_per_week}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{r.tshirt_size}</td>
                  <td className="px-3 py-3 text-sm whitespace-nowrap">
                    {r.panel_apply === "Yes" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FACC15] text-[#0A1128] text-[10px] font-black uppercase tracking-wider">🎤 Yes</span>
                    ) : (
                      <span className="text-[#6B7280]">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-sm whitespace-nowrap">
                    {r.stay_on_property === "Yes" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0066FF]/15 text-[#0066FF] text-[10px] font-black uppercase tracking-wider">🛏️ Yes</span>
                    ) : r.stay_on_property === "No" ? (
                      <span className="text-[#6B7280]">No</span>
                    ) : (
                      <span className="text-[#9CA3AF] italic">{r.stay_on_property || "—"}</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-sm whitespace-nowrap">
                    <span className={r.food_allergies === "Yes" ? "text-amber-600 font-semibold" : "text-[#6B7280]"}>{r.food_allergies || "No"}</span>
                  </td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{r.how_heard || "—"}</td>
                  <td className="px-3 py-3 whitespace-nowrap"><StatusTag status={r.status} /></td>
                  <td className="px-3 py-3 text-right"><ChevronRight size={16} className="text-gray-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <DetailDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected ? `${selected.first_name} ${selected.last_name}` : ""}
      >
        {selected && (
          <div>
            <div className="mb-6">
              <Label className="text-xs font-bold uppercase tracking-widest text-[#6B7280] mb-2 block">Status</Label>
              <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                <SelectTrigger className="h-10" data-testid="reg-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{INT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Row label="Submitted" value={formatDate(selected.created_at)} />
            <Row label="Email" value={selected.email} />
            <Row label="Phone" value={selected.phone} />
            <Row label="City" value={selected.city} />
            <Row label="State" value={selected.state} />
            <Row label="Country" value={selected.country} />
            <Row label="Business Name" value={selected.business_name} />
            <Row label="Years Teaching" value={selected.years_teaching} />
            <Row label="Years in Business" value={selected.years_in_business} />
            <Row label="Students per Week" value={selected.students_per_week} />
            <Row label="Employees" value={selected.employees} />
            <Row label="Business Model" value={selected.business_model} />
            <Row label="How They Heard" value={selected.how_heard} />
            <Row label="T-Shirt Size" value={selected.tshirt_size} />
            <Row label="Food Allergies" value={selected.food_allergies} />
            {selected.food_allergies === "Yes" && <Row label="Allergy Details" value={selected.food_allergies_details} />}
            <Row label="Physical Limitations" value={selected.physical_limitations} />
            {selected.physical_limitations === "Yes" && <Row label="Physical Details" value={selected.physical_limitations_details} />}
            <Row label="Learning Goals" value={selected.learning_goals} />

            {/* Lodging */}
            <div className="mt-6 pt-6 border-t border-[#E5E7EB]">
              <div className="text-[10px] font-black uppercase tracking-[0.25em] text-[#0066FF] mb-3">🛏️ Lodging</div>
              <Row label="Stay on Property?" value={selected.stay_on_property || "Not sure"} />
            </div>

            {/* Panel application (only render block if they applied) */}
            {selected.panel_apply === "Yes" && (
              <div className="mt-6 pt-6 border-t-2 border-[#FACC15]/40 bg-[#FACC15]/5 -mx-1 px-4 py-4 rounded-lg" data-testid="drawer-panel-block">
                <div className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A16207] mb-3">🎤 Panel Application</div>
                <Row label="Applying for Panel" value="Yes" />
                <Row label="Why a great fit" value={selected.panel_why_fit || "—"} />
                <Row label="Benefit to applicant" value={selected.panel_benefit_you || "—"} />
                <Row label="Benefit to group" value={selected.panel_benefit_group || "—"} />
              </div>
            )}
          </div>
        )}
      </DetailDrawer>
    </div>
  );
}

/* -------------------- SPEAKERS TAB -------------------- */
function SpeakersTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ talk_category: "", talk_length: "", has_spoken_before: "", status: "" });
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [notes, setNotes] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search: search || undefined, ...filters, sort_by: sortBy, sort_dir: sortDir };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const { data } = await api.get("/admin/speakers", { params });
      setItems(data.items); setTotal(data.total);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  }, [search, filters, sortBy, sortDir]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setNotes(selected?.notes || ""); }, [selected]);

  const updateField = async (id, body) => {
    try {
      await api.patch(`/admin/speakers/${id}`, body);
      setSelected((s) => s && s.id === id ? { ...s, ...body } : s);
      fetchData();
      toast.success("Updated");
    } catch (e) { toast.error("Update failed"); }
  };

  const sortHeader = (label, key) => (
    <th
      className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280] cursor-pointer hover:text-[#0066FF]"
      onClick={() => { if (sortBy === key) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(key); setSortDir("asc"); } }}
      data-testid={`sort-sp-${key}`}
    >
      <span className="inline-flex items-center gap-1">{label} <ArrowUpDown size={12} /></span>
    </th>
  );

  return (
    <div data-testid="speakers-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display-tight text-2xl text-[#0A1128]">Speaker Applications</h2>
          <p className="text-sm text-[#6B7280] mt-1" data-testid="speakers-total">{total} applications received</p>
        </div>
        <button onClick={() => downloadCsv("/admin/speakers/export")} data-testid="export-speakers"
          className="inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors">
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 mb-4">
        <div className="grid md:grid-cols-5 gap-3">
          <div className="md:col-span-2 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input className="pl-9 h-10" placeholder="Search name, email, business, topic…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="sp-search" />
          </div>
          <Select value={filters.talk_category || "all"} onValueChange={(v) => setFilters({ ...filters, talk_category: v === "all" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>{TALK_CATEGORIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.talk_length || "all"} onValueChange={(v) => setFilters({ ...filters, talk_length: v === "all" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="filter-length"><SelectValue placeholder="Length" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All lengths</SelectItem>{TALK_LENGTHS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.has_spoken_before || "all"} onValueChange={(v) => setFilters({ ...filters, has_spoken_before: v === "all" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="filter-spoken"><SelectValue placeholder="Has spoken" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E5E7EB]">
            <thead className="bg-[#F9FAFB]">
              <tr>
                {sortHeader("Submitted", "created_at")}
                {sortHeader("Name", "first_name")}
                {sortHeader("Email", "email")}
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Phone</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">City/State</th>
                {sortHeader("Business", "business_name")}
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Talk Topic</th>
                {sortHeader("Category", "talk_category")}
                {sortHeader("Length", "talk_length")}
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Spoken?</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Video</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {loading ? (
                <tr><td colSpan={12} className="px-3 py-8 text-center text-sm text-[#6B7280]">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={12} className="px-3 py-8 text-center text-sm text-[#6B7280]">No applications yet.</td></tr>
              ) : items.map((r) => (
                <tr key={r.id} className="hover:bg-[#F9FAFB] cursor-pointer" onClick={() => setSelected(r)} data-testid={`sp-row-${r.id}`}>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{formatDate(r.created_at)}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] font-medium whitespace-nowrap">{r.first_name} {r.last_name}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128]">{r.email}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{r.phone}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{r.city_state}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128]">{r.business_name}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] max-w-xs truncate">{r.talk_topic}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{r.talk_category}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{r.talk_length}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{r.has_spoken_before}</td>
                  <td className="px-3 py-3 text-sm">
                    {r.presentation_video_url ? (
                      <a href={r.presentation_video_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#0066FF] hover:underline">Watch</a>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap"><StatusTag status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <DetailDrawer open={!!selected} onClose={() => setSelected(null)} title={selected ? `${selected.first_name} ${selected.last_name}` : ""}>
        {selected && (
          <div>
            <div className="mb-6">
              <Label className="text-xs font-bold uppercase tracking-widest text-[#6B7280] mb-2 block">Status</Label>
              <Select value={selected.status} onValueChange={(v) => updateField(selected.id, { status: v })}>
                <SelectTrigger className="h-10" data-testid="sp-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SPK_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Row label="Submitted" value={formatDate(selected.created_at)} />
            <Row label="Email" value={selected.email} />
            <Row label="Phone" value={selected.phone} />
            <Row label="City/State" value={selected.city_state} />
            <Row label="Instagram" value={selected.instagram} />
            <Row label="YouTube" value={selected.youtube} />
            <Row label="Website" value={selected.website} />
            <Row label="Business" value={selected.business_name} />
            <Row label="Years in Business" value={selected.years_in_business} />
            <Row label="Students/Wk" value={selected.students_per_week} />
            <Row label="Business Description" value={selected.business_description} />
            <Row label="Talk Topic" value={selected.talk_topic} />
            <Row label="Category" value={selected.talk_category} />
            <Row label="Length" value={selected.talk_length} />
            <Row label="Spoken Before" value={selected.has_spoken_before} />
            {selected.has_spoken_before === "Yes" && <Row label="Where" value={selected.has_spoken_details} />}
            {selected.presentation_video_url && (
              <Row label="Video" value={<a href={selected.presentation_video_url} target="_blank" rel="noreferrer" className="text-[#0066FF] hover:underline">{selected.presentation_video_url}</a>} />
            )}
            <Row label="Why SSU" value={selected.why_ssu} />
            <Row label="Other" value={selected.other_info} />

            <div className="mt-6">
              <Label className="text-xs font-bold uppercase tracking-widest text-[#6B7280] mb-2 block">Admin Notes (internal)</Label>
              <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="sp-notes" />
              <button
                onClick={() => updateField(selected.id, { notes })}
                data-testid="sp-save-notes"
                className="mt-2 bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-semibold px-4 py-2 rounded-md"
              >Save notes</button>
            </div>
          </div>
        )}
      </DetailDrawer>
    </div>
  );
}

/* -------------------- SPONSORS TAB -------------------- */
function SponsorsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ selected_tier: "", status: "" });
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [notes, setNotes] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search: search || undefined, ...filters, sort_by: sortBy, sort_dir: sortDir };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const { data } = await api.get("/admin/sponsors", { params });
      setItems(data.items); setTotal(data.total);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  }, [search, filters, sortBy, sortDir]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setNotes(selected?.notes || ""); }, [selected]);

  const updateField = async (id, body) => {
    try {
      await api.patch(`/admin/sponsors/${id}`, body);
      setSelected((s) => s && s.id === id ? { ...s, ...body } : s);
      fetchData();
      toast.success("Updated");
    } catch (e) { toast.error("Update failed"); }
  };

  const sortHeader = (label, key) => (
    <th
      className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280] cursor-pointer hover:text-[#0066FF]"
      onClick={() => { if (sortBy === key) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(key); setSortDir("asc"); } }}
      data-testid={`sort-sponsor-${key}`}
    >
      <span className="inline-flex items-center gap-1">{label} <ArrowUpDown size={12} /></span>
    </th>
  );

  return (
    <div data-testid="sponsors-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display-tight text-2xl text-[#0A1128]">Sponsor Applications</h2>
          <p className="text-sm text-[#6B7280] mt-1" data-testid="sponsors-total">{total} applications received</p>
        </div>
        <button onClick={() => downloadCsv("/admin/sponsors/export")} data-testid="export-sponsors"
          className="inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors">
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 mb-4">
        <div className="grid md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input className="pl-9 h-10" placeholder="Search company, name, email…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="sponsor-search" />
          </div>
          <Select value={filters.selected_tier || "all"} onValueChange={(v) => setFilters({ ...filters, selected_tier: v === "all" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="filter-sponsor-tier"><SelectValue placeholder="Tier" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tiers</SelectItem>
              {SPONSOR_TIERS_ADMIN.map(t => <SelectItem key={t.slug} value={t.slug}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="filter-sponsor-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {SPONSOR_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E5E7EB]">
            <thead className="bg-[#F9FAFB]">
              <tr>
                {sortHeader("Submitted", "created_at")}
                {sortHeader("Company", "company_name")}
                {sortHeader("Tier", "selected_tier_label")}
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Contact</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Email</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Phone</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Status</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {loading ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-[#6B7280]">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-[#6B7280]">No sponsor applications yet.</td></tr>
              ) : items.map((r) => (
                <tr key={r.id} className="hover:bg-[#F9FAFB] cursor-pointer" onClick={() => setSelected(r)} data-testid={`sponsor-row-${r.id}`}>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{formatDate(r.created_at)}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] font-medium">{r.company_name}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FACC15]/20 text-[#A16207] text-[10px] font-black uppercase tracking-wider">
                      {r.selected_tier_label || r.selected_tier}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{r.first_name} {r.last_name}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128]">{r.email}</td>
                  <td className="px-3 py-3 text-sm text-[#0A1128] whitespace-nowrap">{r.phone}</td>
                  <td className="px-3 py-3 whitespace-nowrap"><StatusTag status={r.status} /></td>
                  <td className="px-3 py-3 text-right"><ChevronRight size={16} className="text-gray-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <DetailDrawer open={!!selected} onClose={() => setSelected(null)} title={selected ? selected.company_name : ""}>
        {selected && (
          <div>
            <div className="mb-6">
              <Label className="text-xs font-bold uppercase tracking-widest text-[#6B7280] mb-2 block">Status</Label>
              <Select value={selected.status} onValueChange={(v) => updateField(selected.id, { status: v })}>
                <SelectTrigger className="h-10" data-testid="sponsor-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SPONSOR_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Row label="Submitted" value={formatDate(selected.created_at)} />
            <Row label="Tier" value={selected.selected_tier_label || selected.selected_tier} />
            <Row label="Company" value={selected.company_name} />
            <Row label="Website" value={selected.company_website ? (
              <a href={selected.company_website} target="_blank" rel="noreferrer" className="text-[#0066FF] hover:underline">{selected.company_website}</a>
            ) : "—"} />
            <Row label="Contact" value={`${selected.first_name} ${selected.last_name}`} />
            <Row label="Title" value={selected.contact_title} />
            <Row label="Email" value={selected.email} />
            <Row label="Phone" value={selected.phone} />
            <Row label="How heard" value={selected.how_heard} />
            <Row label="Products offered" value={selected.products_offered} />
            <Row label="Benefit to group" value={selected.benefit_to_group} />
            <Row label="Other info" value={selected.other_info} />

            <div className="mt-6">
              <Label className="text-xs font-bold uppercase tracking-widest text-[#6B7280] mb-2 block">Admin Notes (internal)</Label>
              <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="sponsor-notes" />
              <button
                onClick={() => updateField(selected.id, { notes })}
                data-testid="sponsor-save-notes"
                className="mt-2 bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-semibold px-4 py-2 rounded-md"
              >Save notes</button>
            </div>
          </div>
        )}
      </DetailDrawer>
    </div>
  );
}

/* -------------------- SETTINGS TAB -------------------- */
function SettingsTab() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [notif, setNotif] = useState("");
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setNotif(data.notification_email || "")).catch(() => {});
    api.get("/admin/audit-log").then(({ data }) => setAudit(data.items)).catch(() => {});
  }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed");
      setCurrent(""); setNext("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };

  const saveNotif = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", { notification_email: notif });
      toast.success("Notification email saved");
    } catch (err) { toast.error("Failed"); }
  };

  return (
    <div data-testid="settings-tab" className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Settings size={18} className="text-[#0066FF]" />
          <h3 className="font-display-tight text-lg text-[#0A1128]">Change Password</h3>
        </div>
        <form onSubmit={changePassword} className="space-y-3">
          <div>
            <Label className="text-sm font-semibold">Current password</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1.5 h-10" data-testid="settings-current-password" required />
          </div>
          <div>
            <Label className="text-sm font-semibold">New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1.5 h-10" data-testid="settings-new-password" required minLength={6} />
          </div>
          <button type="submit" data-testid="settings-save-password" className="bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-semibold px-4 py-2 rounded-md">Update password</button>
        </form>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <FileText size={18} className="text-[#0066FF]" />
          <h3 className="font-display-tight text-lg text-[#0A1128]">Notification Email</h3>
        </div>
        <form onSubmit={saveNotif} className="space-y-3">
          <div>
            <Label className="text-sm font-semibold">Where form submission notifications would be sent</Label>
            <Input type="email" value={notif} onChange={(e) => setNotif(e.target.value)} className="mt-1.5 h-10" data-testid="settings-notif-email" required />
          </div>
          <button type="submit" data-testid="settings-save-notif" className="bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-semibold px-4 py-2 rounded-md">Save</button>
        </form>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 lg:col-span-2">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={18} className="text-[#0066FF]" />
          <h3 className="font-display-tight text-lg text-[#0A1128]">Audit Log</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F9FAFB] text-xs uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Event</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {audit.length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-[#6B7280]">No events yet.</td></tr>
              ) : audit.map((a) => (
                <tr key={`${a.at}-${a.event}-${a.email}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(a.at)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{a.event}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{a.email}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{a.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* -------------------- DASHBOARD SHELL -------------------- */
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("registrations");

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const tabs = [
    { key: "registrations", label: "Registrations", icon: Users },
    { key: "speakers", label: "Speakers", icon: Mic },
    { key: "sponsors", label: "Sponsors", icon: Handshake },
    { key: "waivers", label: "Waivers", icon: ShieldCheck },
    { key: "campaigns", label: "Campaigns", icon: MailPlus },
    { key: "links", label: "Links", icon: LinkIcon },
    { key: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex">
      <aside className="w-64 bg-white border-r border-[#E5E7EB] hidden md:flex flex-col">
        <div className="px-6 py-5 border-b border-[#E5E7EB] flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#FAFAF7] p-1 ring-2 ring-[#FACC15]/40 overflow-hidden">
            <img src="/ssu-logo.jpg" alt="SSU" className="w-full h-full object-contain" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-sm text-[#0A1128]">SSU <span className="text-[#A16207]">WORKSHOPS</span></div>
            <div className="text-[10px] text-[#6B7280] uppercase tracking-widest mt-0.5">Admin Console</div>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${tab === t.key ? "bg-[#0066FF] text-white" : "text-[#0A1128] hover:bg-[#F3F4F6]"}`}
              >
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-[#E5E7EB]">
          <div className="text-xs text-[#6B7280] mb-1">Signed in as</div>
          <div className="text-sm font-semibold text-[#0A1128] truncate" data-testid="admin-user-email">{user?.email}</div>
          <button onClick={onLogout} data-testid="admin-logout" className="mt-3 w-full flex items-center justify-center gap-2 border border-[#E5E7EB] hover:bg-[#F3F4F6] text-sm font-semibold py-2 rounded-md">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="md:hidden bg-white border-b border-[#E5E7EB] px-4 py-3 flex items-center justify-between">
          <div className="font-display text-lg text-[#0A1128]">SSU Admin</div>
          <button onClick={onLogout} className="text-sm font-semibold text-[#0066FF]">Sign out</button>
        </header>
        <div className="md:hidden bg-white border-b border-[#E5E7EB] px-4 py-2 flex gap-2 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold whitespace-nowrap ${tab === t.key ? "bg-[#0066FF] text-white" : "text-[#0A1128] bg-[#F3F4F6]"}`}
            >{t.label}</button>
          ))}
        </div>
        <div className="p-6 lg:p-10">
          {tab === "registrations" && <RegistrationsTab />}
          {tab === "speakers" && <SpeakersTab />}
          {tab === "sponsors" && <SponsorsTab />}
          {tab === "waivers" && <WaiversTab />}
          {tab === "campaigns" && <CampaignsTab />}
          {tab === "links" && <LinksTab />}
          {tab === "settings" && <SettingsTab />}
        </div>
      </main>
    </div>
  );
}
